// Per-outcome label + badge styling for the results popup.
const OUTCOME_META = {
  advanced: {
    label: "Injected",
    badge: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
  },
  skipped_ahead: {
    label: "Already ahead",
    badge: "border-sky-500/40 bg-sky-500/10 text-sky-400",
  },
  blocked_prerequisite: {
    label: "Blocked",
    badge: "border-rose-500/40 bg-rose-500/10 text-rose-400",
  },
  skipped_not_ready: {
    label: "Not ready",
    badge: "border-amber-500/40 bg-amber-500/10 text-amber-400",
  },
  box_error: {
    label: "Box error",
    badge: "border-rose-500/40 bg-rose-500/10 text-rose-400",
  },
  // Entry (noise/decoy/department) injects report `fired` instead of `advanced`.
  fired: {
    label: "Fired",
    badge: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
  },
};

export function getOutcomeMeta(outcome) {
  return (
    OUTCOME_META[outcome] || {
      label: outcome || "Unknown",
      badge: "border-(--color-border) text-(--color-text-muted)",
    }
  );
}

// response_body is the AttackBox runner's raw reply (a JSON string). The reason
// is split across a top-level `error` summary and a nested `detail`, e.g.
// {"detail":{"detail":"a chain is already running; ..."},"error":"runner
// rejected the injection"}. Show both, summary first, without repeating text.
function extractRunnerMessage(responseBody) {
  if (!responseBody) return null;
  let body;
  try {
    body = JSON.parse(responseBody);
  } catch {
    return String(responseBody).trim() || null;
  }
  const detail = typeof body?.detail === "string" ? body.detail : body?.detail?.detail;
  const parts = [];
  for (const candidate of [body?.error, detail, body?.message]) {
    const text = typeof candidate === "string" ? candidate.trim() : "";
    if (text && !parts.includes(text)) parts.push(text);
  }
  return parts.join(": ") || null;
}

function getBoxErrorMessage(result) {
  return (
    result?.error ||
    extractRunnerMessage(result?.response_body) ||
    "AttackBox call failed."
  );
}

// Stages run in order, so a team can only be injected one stage past where it
// is. If more than one stage is missing, spell out the range still to clear.
function explainBlocked(currentStage, targetStage) {
  const current = currentStage ?? 0;
  const firstMissing = current + 1;
  const lastMissing = targetStage - 1;
  if (firstMissing >= lastMissing) {
    return `Still on stage ${current} — complete stage ${lastMissing} before stage ${targetStage} can run.`;
  }
  return `Still on stage ${current} — complete stages ${firstMissing}–${lastMissing} before stage ${targetStage} can run.`;
}

// A per-team reason line for the results popup.
export function explainOutcome(result, stage) {
  switch (result?.outcome) {
    case "advanced":
      return `Stage ${result.current_stage ?? stage} launched${result.public_ip ? ` on ${result.public_ip}` : ""}.`;
    case "skipped_ahead":
      return `Already on stage ${result.current_stage ?? stage} or later.`;
    case "blocked_prerequisite":
      return explainBlocked(result.current_stage, stage);
    case "skipped_not_ready":
      return "AttackBox isn't ready yet.";
    case "box_error":
      return getBoxErrorMessage(result);
    default:
      return "";
  }
}

// Advance local run state for every team whose stage actually ran, so cards
// reflect the inject without a refetch. Shared by the per-team and global flows.
export function applyAdvancedResults(results, onStageAdvanced, fallbackStage) {
  (results || []).forEach((r) => {
    if (r.outcome === "advanced") {
      onStageAdvanced?.(r.run_id, r.current_stage ?? fallbackStage);
    }
  });
}

// Maps a per-team inject outcome to a toast type + message.
// Outcomes come from POST /team-scenario/admin/{scenario_id}/inject.
export function describeInjectOutcome(result, stage) {
  const team = result?.team_name || "Team";
  switch (result?.outcome) {
    case "advanced":
      return { type: "success", message: `Stage ${stage} launched for ${team}.` };
    case "skipped_ahead":
      return {
        type: "info",
        message: `${team} is already on stage ${result.current_stage ?? stage} or later.`,
      };
    case "blocked_prerequisite":
      return {
        type: "error",
        message: `${team} must reach stage ${stage - 1} before stage ${stage} can run.`,
      };
    case "skipped_not_ready":
      return {
        type: "warning",
        message: `${team}'s AttackBox isn't ready yet. Try again shortly.`,
      };
    case "box_error":
      return {
        type: "error",
        message: `${team}: ${getBoxErrorMessage(result)}`,
      };
    default:
      return { type: "info", message: `Inject sent for ${team}.` };
  }
}

// ─── Entry injects (noise / decoy / department) ───────────────────────────────
export function explainEntryOutcome(result) {
  switch (result?.outcome) {
    case "fired":
      return `Fired${result.public_ip ? ` on ${result.public_ip}` : ""}.`;
    case "skipped_not_ready":
      return "AttackBox isn't ready yet.";
    case "box_error":
      return getBoxErrorMessage(result);
    default:
      return "";
  }
}

// Maps a single-team entry outcome to a toast type + message.
export function describeEntryOutcome(result, entryName) {
  const team = result?.team_name || "Team";
  switch (result?.outcome) {
    case "fired":
      return { type: "success", message: `"${entryName}" fired for ${team}.` };
    case "skipped_not_ready":
      return {
        type: "warning",
        message: `${team}'s AttackBox isn't ready yet. Try again shortly.`,
      };
    case "box_error":
      return { type: "error", message: `${team}: ${getBoxErrorMessage(result)}` };
    default:
      return { type: "info", message: `Inject sent for ${team}.` };
  }
}
